import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { RestfulApiService } from '../restful-api.service';

export interface RegionItem {
  uuid: string;
  name: string;
  imageUrl: string | null;
  noOfDeals: number;
  hasDeals: boolean;
  active: boolean;
  countriesCount: number;
  countries: string[] | null;
}

export interface RegionsResponse {
  response: RegionItem[];
}

export interface RegionCountry {
  uuid: string;
  name: string;
  code: string;
  stateList: unknown[];
}

export interface RegionCountriesResponse {
  response: RegionCountry[];
}

export interface RegionCheckNameResponse {
  response: boolean; // true = name already taken
}

export interface RegionCreateUpdatePayload {
  uuid: string | null;
  name: string;
  active: boolean;
  countries: string[]; // country UUIDs
}

export interface RegionDetails {
  uuid: string;
  name: string;
  imageUrl: string | null;
  noOfDeals: number;
  hasDeals: boolean;
  active: boolean;
  countriesCount: number;
  countries: RegionCountry[];
}

export interface RegionDetailsResponse {
  response: RegionDetails;
}

@Injectable({
  providedIn: 'root'
})
export class RegionService {
  private readonly base = '/v1/general/settings/region';
  private readonly listBase = '/v1/general/settings/regions';

  constructor(private apiService: RestfulApiService) {}

  /** Fetch all regions, optionally filtered server-side by search word. */
  getRegions(searchWord = ''): Observable<RegionsResponse> {
    const params = searchWord ? { searchInput: searchWord } : {};
    return this.apiService.get(this.listBase, params) as Observable<RegionsResponse>;
  }

  /** Returns true if the name is already taken. */
  checkName(name: string): Observable<RegionCheckNameResponse> {
    return this.apiService.post(`${this.base}/check-name`, { name }) as Observable<RegionCheckNameResponse>;
  }

  /** Fetch countries not yet assigned to any region. */
  getUnassignedCountries(): Observable<RegionCountriesResponse> {
    return this.apiService.get(`${this.base}/unassigned-countries`) as Observable<RegionCountriesResponse>;
  }

  /** Create or update a region. Pass uuid: null to create. */
  createUpdate(payload: RegionCreateUpdatePayload): Observable<unknown> {
    return this.apiService.post(`${this.base}/create-update`, payload) as Observable<unknown>;
  }

  /** Fetch full region details including assigned countries as objects. */
  getDetails(regionUuid: string): Observable<RegionDetailsResponse> {
    return this.apiService.get(`${this.base}/details/${regionUuid}`) as Observable<RegionDetailsResponse>;
  }

  /** Delete a region by UUID. */
  delete(regionUuid: string): Observable<unknown> {
    return this.apiService.delete(`${this.base}/delete/${regionUuid}`) as Observable<unknown>;
  }
}
